/**
 * DoctypePicker — the canonical surface for doctype selection.
 *
 * Phase 6.2 (2026-04-26), restyled to the launch film (2026-10-14): domain
 * tabs (Pharma · Medical Device · Shared) → rows grouped under mono category
 * headers (CLINICAL · QUALITY · REGULATORY, see ``categoryForEntry``) →
 * bordered rows of mono code · full name · governing guideline. Replaces the conversational chip
 * picker for doctype selection across every entry path.
 *
 * Used by:
 *   - `/draft asking-doctype` step (mid-conversation pick)
 *   - ContextStrip "pick a doctype" chip-click
 *   - Any future doctype-pick command alias
 *
 * Mirrors the LibraryHub design pattern (Fluent Dialog modal, single
 * canonical surface) so writers learn one selection idiom for both
 * special-case concepts (library, doctype). Per the "writer-input
 * pattern" rule (memory feedback): everything else uses user_input;
 * library and doctype are the two named exceptions with their own
 * canonical surfaces.
 *
 * SRP: pure presentation + selection. The picker owns rendering +
 * tab state + filter logic. The CALLER decides what happens
 * on select (start a draft, switch active doctype, etc.). No
 * knowledge of run state, draftFlow, libraries, or LoomFreshSelection.
 *
 * # DECISION: Show ALL catalog entries (including "coming soon"
 * #   ones without ``backendDocType``); render unsupported rows
 * #   in a disabled style with a "coming soon" hint.
 * # WHY: matches the screenshot the writer signed off on. Honest
 * #      UX — writers see the full ecosystem and which entries are
 * #      wired today. Filtering them out (the chip-picker behaviour)
 * #      hides the catalog's breadth and surprises writers when a
 * #      doctype "appears" in a later release. Honest absence beats
 * #      silent omission.
 * # DATE: 2026-04-26
 *
 * # DECISION: Expandable entries (NDA/BLA → M1-M5) render only
 * #   the parent in v1. Children deferred to a follow-up.
 * # WHY: YAGNI — most M-series children don't have ``backendDocType``
 * #      wired today, so adding the expand affordance would surface
 * #      five disabled rows that aren't actionable. When children
 * #      get wired, add the expand toggle then. Parent rows stay
 * #      visible + selectable when the parent itself is wired.
 * # DATE: 2026-04-26
 */

import React, { useMemo, useState } from "react";
import type { AppConfig, DocTypeListEntry, DoctypeAbout as DoctypeAboutData } from "../../api/client";
import type { Workspace } from "../../types";
import {
  DOC_CATALOG,
  DOC_CATEGORY_ORDER,
  DOMAIN_LABELS,
  type DocCatalogEntry,
  type DocCategory,
  type DocCatalogPhase,
  type DocDomain,
  categoryForEntry,
  isPrivateTemplateCode,
  phasesForDomain,
  privateTemplateName,
} from "../doc-catalog";
import { type DoctypeChoice, SUBMISSION_REGIONS } from "../draftFlow";
import { Button, ChoiceList, Eyebrow, SearchField, Select, StepHeader, Tabs } from "../../ds/components";
import { DoctypeAbout, type DoctypeAboutState } from "./DoctypeAbout";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface DoctypePickerProps {
  /** Modal visibility. False = closed; true = open. */
  open: boolean;
  /** Currently-selected doctype code (highlighted in the list).
   *  Null when nothing is selected yet (idle / first pick). */
  selectedDocCode: string | null;
  /** Backend runtime config. Drives sandbox-availability filtering
   *  + the unsupported-entry "coming soon" hint. Null = treat all
   *  entries as supported (live mode, no filtering). */
  appConfig: AppConfig | null;
  /** Restrict the visible catalog to these doctype codes.
   *
   *  ``null`` / omitted = no restriction, the full catalog renders (the
   *  normal case). A non-null list narrows the picker to those codes:
   *  non-matching entries, now-empty phase groups, and now-empty domain
   *  tabs are all dropped.
   *
   *  Resolved in VariantCShell from the backend's tier-filtered
   *  ``GET /doc-types`` (:func:`useAllowedDocCodes`) — a ``pay_per_use`` account gets
   *  only the ``doctypes/doc_pricing.json`` codes, paid tiers get all. Passed
   *  ``[]`` while that load is in flight / on error, so the picker fails
   *  closed (shows nothing) rather than falling open to the full catalog.
   *  Entries are FILTERED OUT rather than disabled — the file's existing
   *  "show everything, disable what isn't wired" decision is about honest
   *  absence within the product's scope, but a reviewer looking at a page of
   *  greyed rows reads it as an unfinished add-in, which is the thing
   *  AppSource certification rejects. */
  allowedDocCodes?: readonly string[] | null;
  /** Writer picked a doctype. Caller decides what to do with the
   *  choice (start a draft, just update active, etc.). */
  onSelect: (choice: DoctypeChoice) => void;
  /** Writer cancelled (Cancel button or Escape). */
  onCancel: () => void;
  /** When true, the picker is being used to identify the type of an EXISTING
   *  document (build-on / custom submission) rather than choosing what to draft.
   *  Only changes the header copy. */
  customImport?: boolean;
  /** Workspaces the writer belongs to. When given (with ``onWorkspaceChange``)
   *  a workspace selector heads the dialog, so the writer can see each
   *  workspace's private templates. Selecting one IS the shell's workspace
   *  selection — the run is started in the workspace the template came from. */
  workspaces?: readonly Workspace[];
  selectedWorkspaceId?: string | null;
  onWorkspaceChange?: (workspaceId: string) => void;
  /** Private templates linked to ``selectedWorkspaceId`` (from
   *  ``GET /doc-types?workspace_id=``). ``null`` while loading. Rendered on a
   *  "Workspace templates" tab ahead of the public catalog. */
  privateTemplates?: readonly DocTypeListEntry[] | null;
  /** Submission regions to offer (``{code: label}`` from ``GET /doc-types``).
   *  Empty / omitted falls back to ``SUBMISSION_REGIONS``. */
  regions?: Readonly<Record<string, string>> | null;
  /** Open straight on the region step for this doctype — used when a draft
   *  starts from a saved doctype that has no region yet. */
  regionStepFor?: DoctypeChoice | null;
  /** Loads what a doctype's "i" shows (guidance, what the drafting agent can
   *  do, expected sources) for a backend doctype code. Omitted = no "i". */
  loadDoctypeAbout?: (backendDocType: string) => Promise<DoctypeAboutData>;
}

/** Tab keys: the domain tabs (Pharma · Medical Device · Shared), plus a
 *  "Workspace templates" tab ahead of them when the selected workspace has
 *  private templates. */
const WORKSPACE_TAB = "workspace" as const;
type PickerTab = DocDomain | typeof WORKSPACE_TAB;

// ---------------------------------------------------------------------------
// Pure helper — exported for tests
// ---------------------------------------------------------------------------

/** Compute whether a catalog entry is selectable in the given app
 *  config. Pure: same inputs → same output. */
export function isDoctypeAvailable(entry: DocCatalogEntry, appConfig: AppConfig | null): boolean {
  if (!entry.backendDocType) return false;
  if (!appConfig?.sandbox) return true;
  const sandboxList = appConfig.sandbox_available_doc_types ?? [];
  return sandboxList.includes(entry.backendDocType);
}

/** Phases for a domain, narrowed to ``allowedDocCodes``.
 *
 *  ``null``/undefined returns the domain's phases untouched. Otherwise
 *  each phase keeps only its matching docs, and phases left with no docs
 *  are dropped so no empty group header renders. Pure. */
export function visiblePhasesForDomain(
  domain: DocDomain,
  allowedDocCodes?: readonly string[] | null,
): DocCatalogPhase[] {
  const phases = phasesForDomain(domain);
  if (!allowedDocCodes) return phases;
  const allowed = new Set(allowedDocCodes);
  return phases
    .map((phase) => ({ ...phase, docs: phase.docs.filter((entry) => allowed.has(entry.code)) }))
    .filter((phase) => phase.docs.length > 0);
}

/** Domains that still have at least one visible doc under
 *  ``allowedDocCodes``. Used to drop domain tabs that would open onto an
 *  empty list. Pure. */
export function visibleDomains(allowedDocCodes?: readonly string[] | null): DocDomain[] {
  const domains = Object.keys(DOMAIN_LABELS) as DocDomain[];
  if (!allowedDocCodes) return domains;
  return domains.filter((domain) => visiblePhasesForDomain(domain, allowedDocCodes).length > 0);
}

/** One row of the flat, category-grouped list: the entry plus the domain
 *  it came from (``entryToChoice`` needs the domain). */
export interface CatalogRow {
  entry: DocCatalogEntry;
  domain: DocDomain;
}

/** The catalog grouped by ``DocCategory`` (launch-film screen 1), narrowed
 *  to ``allowedDocCodes`` and, when ``onlyDomain`` is given, to that one
 *  domain tab.
 *
 *  Walks pharma → device → shared in phase order, so rows keep their
 *  catalog order inside a category. A code listed in two domains (IB sits
 *  under pharma AND shared) renders once — the first, which is also what
 *  ``lookupDoctypeChoice`` resolves it to. Empty categories are dropped.
 *  Pure. */
export function catalogGroups(
  allowedDocCodes?: readonly string[] | null,
  onlyDomain?: DocDomain,
): Array<{ category: DocCategory; rows: CatalogRow[] }> {
  const byCategory = new Map<DocCategory, CatalogRow[]>();
  const seen = new Set<string>();
  const domains = onlyDomain ? [onlyDomain] : (Object.keys(DOMAIN_LABELS) as DocDomain[]);
  for (const domain of domains) {
    for (const phase of visiblePhasesForDomain(domain, allowedDocCodes)) {
      for (const entry of phase.docs) {
        if (seen.has(entry.code)) continue;
        seen.add(entry.code);
        const category = categoryForEntry(entry, domain, phase.phase);
        const rows = byCategory.get(category) ?? [];
        rows.push({ entry, domain });
        byCategory.set(category, rows);
      }
    }
  }
  return DOC_CATEGORY_ORDER.filter((c) => byCategory.has(c)).map((category) => ({
    category,
    rows: byCategory.get(category) ?? [],
  }));
}

/** Convert a catalog entry to the DoctypeChoice payload onSelect
 *  emits. Pure. Caller invokes only when ``isDoctypeAvailable``
 *  returned true; this helper trusts the caller to honour that. */
export function entryToChoice(entry: DocCatalogEntry, domain: DocDomain): DoctypeChoice {
  return {
    docCode: entry.code,
    backendDocType: entry.backendDocType ?? "",
    docFramework: entry.framework ?? null,
    docName: entry.name,
    domain,
    auth: entry.auth ?? null,
  };
}

/** The ``DoctypeChoice`` for a private workspace template. Its UI code is its
 *  backend code (``WS_*``); it lives outside the static catalog. Pure. */
export function privateTemplateChoice(code: string, name: string): DoctypeChoice {
  return {
    docCode: code,
    backendDocType: code,
    docFramework: null,
    docName: name,
    domain: "shared",
    auth: null,
  };
}

/** Find which domain (pharma/device/shared) currently contains the
 *  selected doctype code. Falls back to ``"pharma"`` when not found
 *  or when no selection exists. Pure. */
export function domainForDocCode(docCode: string | null): DocDomain {
  if (!docCode) return "pharma";
  const domains: DocDomain[] = ["pharma", "device", "shared"];
  for (const domain of domains) {
    for (const phase of DOC_CATALOG[domain]) {
      for (const entry of phase.docs) {
        if (entry.code === docCode) return domain;
        if (entry.children?.some((c) => c.code === docCode)) return domain;
      }
    }
  }
  return "pharma";
}

/** Walk the catalog to recover the full ``DoctypeChoice`` payload
 *  for a stored doctype code. Pure: same inputs → same output, no
 *  React. Returns null when the code isn't in the catalog (e.g. the
 *  catalog was edited and the writer's stored selection became
 *  stale) — caller MUST treat that as "no doctype selected" rather
 *  than fabricating a partial choice.
 *
 *  Phase 7.2 — independent ``selectedDocCode`` state in the shell
 *  stores only the code string; the rest of the choice is derived
 *  here on demand. Single source of truth = the catalog. */
export function lookupDoctypeChoice(docCode: string | null): DoctypeChoice | null {
  if (!docCode) return null;
  // A private workspace template is not in the static catalog; its code is
  // its backend doc_type. Never null for one, so a stored selection survives
  // even before the workspace's template list has loaded.
  if (isPrivateTemplateCode(docCode)) return privateTemplateChoice(docCode, privateTemplateName(docCode) ?? docCode);
  const domains: DocDomain[] = ["pharma", "device", "shared"];
  for (const domain of domains) {
    for (const phase of DOC_CATALOG[domain]) {
      for (const entry of phase.docs) {
        if (entry.code === docCode) return entryToChoice(entry, domain);
        if (entry.children) {
          for (const child of entry.children) {
            if (child.code === docCode) return entryToChoice(child, domain);
          }
        }
      }
    }
  }
  return null;
}

/** The ``[code, label]`` region options to render, in server order, falling
 *  back to ``SUBMISSION_REGIONS`` when the server sent none. Pure. */
export function regionOptions(regions?: Readonly<Record<string, string>> | null): Array<[string, string]> {
  const entries = Object.entries(regions ?? {});
  return entries.length > 0 ? entries : Object.entries(SUBMISSION_REGIONS);
}

/** Split a region label ("United Kingdom — MHRA") into the place and the
 *  agency that assesses it. A label without an agency ("Multiple regions")
 *  keeps an empty agency. Pure. */
export function splitRegionLabel(label: string): { place: string; agency: string } {
  const at = label.indexOf(" — ");
  if (at < 0) return { place: label.trim(), agency: "" };
  return { place: label.slice(0, at).trim(), agency: label.slice(at + 3).trim() };
}

/** "US · FDA" for a region code, or the code alone when it has no agency. */
export function regionTag(code: string | null | undefined, regions?: Readonly<Record<string, string>> | null): string | null {
  if (!code) return null;
  const label = regionOptions(regions).find(([c]) => c === code)?.[1];
  const agency = label ? splitRegionLabel(label).agency.split(" / ")[0] : "";
  return agency ? `${code} · ${agency}` : code;
}

/** Second line for the region with no single agency. */
const GLOBAL_REGION_DETAIL = "Filed with more than one regulator";

/** Tab label in the design's sentence case ("Medical device"). */
function domainTabLabel(domain: DocDomain): string {
  const label = DOMAIN_LABELS[domain].label;
  return label.charAt(0) + label.slice(1).toLowerCase();
}

/** Case-insensitive match on name, code, framework and authority. */
export function matchesDoctypeQuery(entry: DocCatalogEntry, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [entry.name, entry.code, entry.framework, entry.auth].some((v) => v?.toLowerCase().includes(q));
}

/** "Continue with US · FDA" — the region step's primary label. */
export function regionContinueLabel(code: string | null, label: string | null): string {
  if (!code || !label) return "Continue";
  const { agency } = splitRegionLabel(label);
  return agency ? `Continue with ${code} · ${agency.split(" / ")[0]}` : `Continue with ${splitRegionLabel(label).place}`;
}

// ---------------------------------------------------------------------------
// Component — boards "03 New document · step 1" and "04 · step 2, region"
// ---------------------------------------------------------------------------

export const DoctypePicker: React.FC<DoctypePickerProps> = ({
  open,
  selectedDocCode,
  appConfig,
  allowedDocCodes = null,
  onSelect,
  onCancel,
  customImport = false,
  workspaces,
  selectedWorkspaceId = null,
  onWorkspaceChange,
  privateTemplates = null,
  regions = null,
  regionStepFor = null,
  loadDoctypeAbout,
}) => {
  // Two steps: pick the doctype, then where it is submitted. The doctype is
  // held here until a region is chosen — nothing is emitted before both land.
  const [pendingChoice, setPendingChoice] = useState<DoctypeChoice | null>(null);
  // Step 1 selection (radio) before Continue.
  const [picked, setPicked] = useState<DoctypeChoice | null>(null);
  const [region, setRegion] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  // The doctype "i": one row's details open at a time, each loaded once.
  const [aboutOpen, setAboutOpen] = useState<string | null>(null);
  const [aboutByCode, setAboutByCode] = useState<Record<string, DoctypeAboutState>>({});
  const toggleAbout = (backendDocType: string): void => {
    if (aboutOpen === backendDocType) {
      setAboutOpen(null);
      return;
    }
    setAboutOpen(backendDocType);
    const known = aboutByCode[backendDocType];
    if (!loadDoctypeAbout || (known && known !== "error")) return;
    setAboutByCode((prev) => ({ ...prev, [backendDocType]: "loading" }));
    loadDoctypeAbout(backendDocType).then(
      (about) => setAboutByCode((prev) => ({ ...prev, [backendDocType]: about })),
      () => setAboutByCode((prev) => ({ ...prev, [backendDocType]: "error" })),
    );
  };
  React.useEffect(() => {
    if (!open) return;
    setPendingChoice(regionStepFor);
    setPicked(lookupDoctypeChoice(selectedDocCode));
    setRegion(null);
    setQuery("");
    // Re-seeded on open only; selectedDocCode changes while open are the
    // writer's own picks echoing back.
    // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  }, [open, regionStepFor]);
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onCancel]);
  const showWorkspaceSelect = !!onWorkspaceChange && !!workspaces && workspaces.length > 0;
  const templates = privateTemplates ?? [];
  const hasTemplates = templates.length > 0;

  // # DECISION: keep the domain tabs at the top, with category-grouped rows
  //   inside each tab.
  // # WHY: a customer drafts either pharma or device documents, so the
  //   domain is the first choice they make (Guru, 2026-10-14).
  // # DATE: 2026-10-14
  const domains = useMemo(() => visibleDomains(allowedDocCodes), [allowedDocCodes]);

  // A workspace with templates opens on them: they are what that workspace
  // was set up to draft. Otherwise open on the selection's own domain.
  const initialTab = useMemo((): PickerTab => {
    void open;
    if (hasTemplates && (!selectedDocCode || isPrivateTemplateCode(selectedDocCode))) return WORKSPACE_TAB;
    const preferred = domainForDocCode(selectedDocCode);
    return domains.includes(preferred) ? preferred : (domains[0] ?? "pharma");
  }, [selectedDocCode, open, hasTemplates, domains]);
  const [tab, setTab] = useState<PickerTab>(initialTab);
  React.useEffect(() => {
    if (open) setTab(initialTab);
  }, [open, initialTab]);

  // Derived rather than stored, so templates disappearing mid-session can't
  // strand the picker on an empty templates view.
  const onWorkspaceTab = tab === WORKSPACE_TAB && showWorkspaceSelect;
  const activeDomain: DocDomain = tab !== WORKSPACE_TAB && domains.includes(tab) ? tab : (domains[0] ?? "pharma");
  const groups = useMemo(
    () =>
      catalogGroups(allowedDocCodes, activeDomain)
        .map((g) => ({ ...g, rows: g.rows.filter((r) => matchesDoctypeQuery(r.entry, query)) }))
        .filter((g) => g.rows.length > 0),
    [allowedDocCodes, activeDomain, query],
  );
  // The design puts the writer's own templates after the public domains.
  // "My templates" is always offered with the workspace selector, so the
  // writer sees where a workspace's private templates live even when empty.
  const tabKeys: PickerTab[] = showWorkspaceSelect ? [...domains, WORKSPACE_TAB] : domains;

  const options = regionOptions(regions);
  const backToCatalog = (): void => {
    setPendingChoice(null);
    setRegion(null);
  };

  if (!open) return null;

  const stepTotal = customImport ? 3 : 5;

  if (pendingChoice) {
    const regionLabel = options.find(([code]) => code === region)?.[1] ?? null;
    return (
      <div className="as-takeover" role="dialog" aria-modal="true" aria-label="Where will this be submitted?">
        <StepHeader
          onBack={regionStepFor ? undefined : backToCatalog}
          onClose={regionStepFor ? onCancel : undefined}
          step={`New document · step 2 of ${stepTotal}`}
          title="Where will this be submitted?"
        >
          <p className="as-p">Drafting applies this region's requirements and reads its guidance.</p>
          <div className="as-filing">
            <span>
              <span className="as-filing-k">Filing </span>
              <b>{pendingChoice.docName}</b>{" "}
              {pendingChoice.docFramework ? <span className="as-filing-fw">{pendingChoice.docFramework}</span> : null}
            </span>
            <button type="button" className="as-linkbtn" aria-label="Change document type" onClick={backToCatalog}>
              Change
            </button>
          </div>
        </StepHeader>
        <div className="as-scroll as-sunken as-takeover-body">
          <ChoiceList
            name="region"
            label="Submission region"
            value={region}
            onChange={setRegion}
            options={options.map(([code, label]) => {
              const { place, agency } = splitRegionLabel(label);
              return { id: code, label: place, meta: agency || (code === "GLOBAL" ? GLOBAL_REGION_DETAIL : undefined) };
            })}
          />
        </div>
        <div className="as-footer">
          <div className="as-footer-actions">
            <Button variant="secondary" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!region}
              onClick={() => {
                if (region) onSelect({ ...pendingChoice, region });
              }}
            >
              {regionContinueLabel(region, regionLabel)}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const tabItems = tabKeys.map((key) => ({
    id: key,
    label: key === WORKSPACE_TAB ? "My templates" : domainTabLabel(key),
    count: key === WORKSPACE_TAB && hasTemplates ? templates.length : undefined,
  }));
  const pickedCode = picked?.docCode ?? null;

  return (
    <div
      className="as-takeover"
      role="dialog"
      aria-modal="true"
      aria-label={customImport ? "Which type of document is this?" : "What are you writing?"}
    >
      <StepHeader
        onClose={onCancel}
        step={`New document · step 1 of ${stepTotal}`}
        title={customImport ? "Which type of document is this?" : "What are you writing?"}
      >
        {showWorkspaceSelect && (
          <Select
            inline
            label="Workspace"
            id="doctype-picker-workspace"
            value={selectedWorkspaceId ?? ""}
            onChange={(e) => {
              if (e.target.value) onWorkspaceChange?.(e.target.value);
            }}
          >
            {!selectedWorkspaceId && <option value="">Choose a workspace…</option>}
            {workspaces?.map((ws) => (
              <option key={ws.id} value={ws.id}>
                {ws.name}
              </option>
            ))}
          </Select>
        )}
        <SearchField value={query} onChange={setQuery} placeholder="Search document types or guidance" />
        {tabItems.length > 1 && (
          <div className="as-stephead-tabs">
            <Tabs items={tabItems} value={onWorkspaceTab ? WORKSPACE_TAB : activeDomain} onChange={setTab} />
          </div>
        )}
      </StepHeader>

      <div className="as-scroll as-sunken as-takeover-body as-stack">
        {onWorkspaceTab ? (
          privateTemplates === null ? (
            <p className="as-note">Loading templates…</p>
          ) : !hasTemplates ? (
            <p className="as-note">No private templates in this workspace.</p>
          ) : (
            <ChoiceList
              name="doctype-template"
              label="Workspace templates"
              value={pickedCode}
              onChange={(code) => {
                const tpl = templates.find((t) => t.code === code);
                if (!tpl) return;
                setPicked(privateTemplateChoice(tpl.code, tpl.display_name || privateTemplateName(tpl.code) || tpl.code));
              }}
              options={templates
                .filter((t) => !query || (t.display_name ?? t.code).toLowerCase().includes(query.trim().toLowerCase()))
                .map((tpl) => ({
                  id: tpl.code,
                  label: tpl.display_name || privateTemplateName(tpl.code) || tpl.code,
                  meta: [
                    tpl.base_doc_type,
                    `v${tpl.version}`,
                    typeof tpl.draftable_count === "number" ? `${tpl.draftable_count} sections to draft` : null,
                  ]
                    .filter(Boolean)
                    .join(" · "),
                }))}
            />
          )
        ) : groups.length === 0 ? (
          <p className="as-note">No document type matches “{query}”.</p>
        ) : (
          groups.map(({ category, rows }) => (
            <section key={category} className="as-stack-8" aria-label={category}>
              <Eyebrow>{category}</Eyebrow>
              <ChoiceList
                name={`doctype-${category}`}
                label="Document type"
                value={pickedCode}
                onChange={(code) => {
                  const row = rows.find((r) => r.entry.code === code);
                  if (row && isDoctypeAvailable(row.entry, appConfig)) setPicked(entryToChoice(row.entry, row.domain));
                }}
                options={rows.map(({ entry }) => {
                  const available = isDoctypeAvailable(entry, appConfig);
                  const line = [entry.code, entry.framework ?? entry.auth, available ? null : "coming soon"]
                    .filter(Boolean)
                    .join(" · ");
                  const backend = available && loadDoctypeAbout ? entry.backendDocType : undefined;
                  const showAbout = backend !== undefined && aboutOpen === backend;
                  return {
                    id: entry.code,
                    label: entry.name,
                    meta: showAbout ? (
                      <>
                        {line}
                        <DoctypeAbout about={aboutByCode[backend] ?? "loading"} />
                      </>
                    ) : (
                      line
                    ),
                    disabled: !available,
                    ...(backend
                      ? {
                          onMore: () => toggleAbout(backend),
                          moreIcon: "info" as const,
                          moreLabel: `About ${entry.name}`,
                          moreExpanded: showAbout,
                        }
                      : {}),
                  };
                })}
              />
            </section>
          ))
        )}
      </div>

      <div className="as-footer">
        <div className="as-footer-actions">
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!picked} onClick={() => picked && setPendingChoice(picked)}>
            Continue
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DoctypePicker;
